// ============================================
// DATA - Synced with touchstone-AI-database
// https://touchstone-ai-database.vercel.app/
// ============================================

// Import the normalized UI export so the app and AEO artifacts
// read from the same neutralized tool records.
import uiExport from './uiExport.js';

// ============================================
// ROBUST LOGO SYSTEM (prevents logo loss)
// ============================================

// Create maps for quick logo lookup (case-insensitive)
const logoMap = new Map();
const logoMapLower = new Map(); // Fallback for case mismatches

uiExport.tools.forEach(tool => {
  if (tool.logo_base64) {
    logoMap.set(tool.name, tool.logo_base64);
    logoMapLower.set(tool.name.toLowerCase(), tool.logo_base64);
  }
});

// Get base64 logo for a tool by name (with fallbacks)
export const getLogoBase64 = (name) => {
  if (!name) return null;
  
  // Exact match
  if (logoMap.has(name)) return logoMap.get(name);
  
  // Case-insensitive fallback
  const lower = name.toLowerCase();
  if (logoMapLower.has(lower)) return logoMapLower.get(lower);
  
  // Partial match fallback (for slight name variations)
  for (const [key, value] of logoMap) {
    if (key.toLowerCase().includes(lower) || lower.includes(key.toLowerCase())) {
      if (import.meta.env?.DEV) console.warn(`⚠️ Logo name mismatch: "${name}" matched with "${key}"`);
      return value;
    }
  }
  
  return null;
};

// Validate logos on module load (development only)
if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  const totalLogos = logoMap.size;
  console.log(`📦 Loaded ${totalLogos} tool logos from ui_export.json`);
}

// Comprehensive capability dimensions
export const allDimensions = [
  'Output Quality',      // How good is the final output
  'Accuracy',            // Factual correctness, precision
  'Speed',               // Latency, throughput
  'Cost Efficiency',     // Price per unit of work
  'API Robustness',      // Uptime, rate limits, documentation
  'Integration Ease',    // SDK quality, ecosystem support
  'Customization',       // Fine-tuning, parameters, control
  'Context Window',      // How much input it can handle
  'Multimodal',          // Support for multiple input/output types
  'Enterprise Ready',    // SOC2, SLAs, support
  'Open Source',         // Can self-host, modify
  'Streaming',           // Real-time output support
  'Batch Processing',    // Bulk operations
  'Version Control',     // Model versioning, reproducibility
];

// Task definitions with specific capability requirements and weights
export const taskDefinitions = {
  'write-content': { 
    label: 'Write content & copy', 
    categories: ['Language Model'], 
    requirements: {
      'Output Quality': { weight: 2.0, min: 'strong' },
      'Accuracy': { weight: 1.5, min: 'moderate' },
      'Customization': { weight: 1.3, min: 'moderate' },
      'Speed': { weight: 0.8, min: 'limited' },
      'Cost Efficiency': { weight: 1.0, min: 'limited' },
      'Context Window': { weight: 1.2, min: 'moderate' },
    },
    industry: 'Marketing' 
  },
  'generate-images': { 
    label: 'Generate images', 
    categories: ['Image Generation'], 
    requirements: {
      'Output Quality': { weight: 2.5, min: 'strong' },
      'Customization': { weight: 1.8, min: 'moderate' },
      'Speed': { weight: 1.0, min: 'moderate' },
      'Cost Efficiency': { weight: 1.2, min: 'limited' },
      'API Robustness': { weight: 1.0, min: 'moderate' },
      'Batch Processing': { weight: 0.8, min: 'limited' },
    },
    industry: 'Marketing' 
  },
  'create-videos': { 
    label: 'Create videos', 
    categories: ['Video Generation'], 
    requirements: {
      'Output Quality': { weight: 2.2, min: 'moderate' },
      'Customization': { weight: 1.5, min: 'moderate' },
      'Speed': { weight: 0.6, min: 'limited' },
      'Cost Efficiency': { weight: 0.8, min: 'limited' },
      'API Robustness': { weight: 1.2, min: 'moderate' },
      'Multimodal': { weight: 1.5, min: 'moderate' },
    },
    industry: 'Marketing' 
  },
  'generate-voice': { 
    label: 'Generate voiceovers', 
    categories: ['Audio/Voice'], 
    requirements: {
      'Output Quality': { weight: 2.3, min: 'strong' },
      'Customization': { weight: 1.6, min: 'moderate' },
      'Speed': { weight: 1.2, min: 'moderate' },
      'Streaming': { weight: 1.4, min: 'moderate' },
      'API Robustness': { weight: 1.0, min: 'moderate' },
      'Cost Efficiency': { weight: 0.9, min: 'limited' },
    },
    industry: 'Marketing' 
  },
  'write-code': { 
    label: 'Write & debug code', 
    categories: ['Code Assistant', 'Language Model'], 
    requirements: {
      'Accuracy': { weight: 2.5, min: 'strong' },
      'Output Quality': { weight: 1.8, min: 'strong' },
      'Context Window': { weight: 2.0, min: 'strong' },
      'Speed': { weight: 1.3, min: 'moderate' },
      'Integration Ease': { weight: 1.5, min: 'moderate' },
      'Customization': { weight: 1.0, min: 'moderate' },
    },
    industry: 'Engineering' 
  },
  'build-agent': { 
    label: 'Build AI agents', 
    categories: ['Agents', 'Orchestration', 'Language Model'], 
    requirements: {
      'Integration Ease': { weight: 2.2, min: 'strong' },
      'API Robustness': { weight: 2.0, min: 'strong' },
      'Accuracy': { weight: 1.8, min: 'moderate' },
      'Streaming': { weight: 1.5, min: 'moderate' },
      'Version Control': { weight: 1.3, min: 'moderate' },
      'Open Source': { weight: 1.0, min: 'limited' },
    },
    industry: 'Engineering' 
  },
  'build-rag': { 
    label: 'Build RAG systems', 
    categories: ['Vector Database', 'Language Model', 'Orchestration', 'Embeddings'], 
    requirements: {
      'Accuracy': { weight: 2.3, min: 'strong' },
      'Integration Ease': { weight: 2.0, min: 'strong' },
      'Speed': { weight: 1.5, min: 'moderate' },
      'Context Window': { weight: 1.8, min: 'strong' },
      'Batch Processing': { weight: 1.2, min: 'moderate' },
      'Enterprise Ready': { weight: 1.0, min: 'moderate' },
    },
    industry: 'Engineering' 
  },
  'test-prompts': { 
    label: 'Test prompts', 
    categories: ['Evaluation'], 
    requirements: {
      'Accuracy': { weight: 2.5, min: 'strong' },
      'Integration Ease': { weight: 1.8, min: 'moderate' },
      'Batch Processing': { weight: 2.0, min: 'strong' },
      'Version Control': { weight: 1.5, min: 'moderate' },
      'Open Source': { weight: 1.2, min: 'moderate' },
      'Cost Efficiency': { weight: 1.0, min: 'moderate' },
    },
    industry: 'Engineering' 
  },
  'monitor-llm': { 
    label: 'Monitor LLMs', 
    categories: ['Observability'], 
    requirements: {
      'Integration Ease': { weight: 2.2, min: 'strong' },
      'Enterprise Ready': { weight: 2.0, min: 'strong' },
      'Speed': { weight: 1.5, min: 'strong' },
      'Batch Processing': { weight: 1.3, min: 'moderate' },
      'Version Control': { weight: 1.2, min: 'moderate' },
      'Cost Efficiency': { weight: 1.0, min: 'moderate' },
    },
    industry: 'Engineering' 
  },
  'analyze-calls': { 
    label: 'Analyze calls', 
    categories: ['Speech Recognition', 'Language Model'], 
    requirements: {
      'Accuracy': { weight: 2.5, min: 'strong' },
      'Speed': { weight: 1.8, min: 'strong' },
      'Context Window': { weight: 1.5, min: 'moderate' },
      'Batch Processing': { weight: 1.3, min: 'moderate' },
      'Enterprise Ready': { weight: 1.2, min: 'moderate' },
      'Cost Efficiency': { weight: 1.0, min: 'moderate' },
    },
    industry: 'Sales' 
  },
  'research-leads': { 
    label: 'Research prospects', 
    categories: ['Search', 'Language Model'], 
    requirements: {
      'Accuracy': { weight: 2.3, min: 'strong' },
      'Speed': { weight: 1.5, min: 'moderate' },
      'Integration Ease': { weight: 1.3, min: 'moderate' },
      'Cost Efficiency': { weight: 1.2, min: 'moderate' },
      'API Robustness': { weight: 1.0, min: 'moderate' },
      'Batch Processing': { weight: 0.8, min: 'limited' },
    },
    industry: 'Sales' 
  },
  'write-outreach': { 
    label: 'Write outreach', 
    categories: ['Language Model'], 
    requirements: {
      'Output Quality': { weight: 2.0, min: 'strong' },
      'Customization': { weight: 1.8, min: 'strong' },
      'Speed': { weight: 1.5, min: 'moderate' },
      'Cost Efficiency': { weight: 1.3, min: 'moderate' },
      'Batch Processing': { weight: 1.2, min: 'moderate' },
      'API Robustness': { weight: 1.0, min: 'moderate' },
    },
    industry: 'Sales' 
  },
  'edit-photos': { 
    label: 'Edit photos', 
    categories: ['Image Generation'], 
    requirements: {
      'Output Quality': { weight: 2.3, min: 'strong' },
      'Customization': { weight: 2.0, min: 'strong' },
      'Speed': { weight: 1.2, min: 'moderate' },
      'Multimodal': { weight: 1.5, min: 'strong' },
      'Batch Processing': { weight: 1.0, min: 'moderate' },
      'Cost Efficiency': { weight: 0.8, min: 'limited' },
    },
    industry: 'Creative' 
  },
  'create-music': { 
    label: 'Create music', 
    categories: ['Audio/Voice'], 
    requirements: {
      'Output Quality': { weight: 2.5, min: 'strong' },
      'Customization': { weight: 2.0, min: 'strong' },
      'Speed': { weight: 0.8, min: 'limited' },
      'API Robustness': { weight: 1.0, min: 'moderate' },
      'Cost Efficiency': { weight: 0.7, min: 'limited' },
      'Streaming': { weight: 1.2, min: 'moderate' },
    },
    industry: 'Creative' 
  },
  'design-assets': { 
    label: 'Design assets', 
    categories: ['Image Generation'], 
    requirements: {
      'Output Quality': { weight: 2.2, min: 'strong' },
      'Customization': { weight: 2.0, min: 'strong' },
      'Batch Processing': { weight: 1.5, min: 'moderate' },
      'Speed': { weight: 1.3, min: 'moderate' },
      'API Robustness': { weight: 1.2, min: 'moderate' },
      'Cost Efficiency': { weight: 1.0, min: 'moderate' },
    },
    industry: 'Creative' 
  },
  'add-safety': { 
    label: 'Add guardrails', 
    categories: ['Guardrails'], 
    requirements: {
      'Accuracy': { weight: 2.8, min: 'strong' },
      'Speed': { weight: 2.0, min: 'strong' },
      'Integration Ease': { weight: 1.8, min: 'strong' },
      'Enterprise Ready': { weight: 1.5, min: 'moderate' },
      'Customization': { weight: 1.3, min: 'moderate' },
      'Open Source': { weight: 1.0, min: 'moderate' },
    },
    industry: 'Enterprise' 
  },
  'manage-models': { 
    label: 'Manage models', 
    categories: ['Deployment', 'Observability'], 
    requirements: {
      'Enterprise Ready': { weight: 2.5, min: 'strong' },
      'Integration Ease': { weight: 2.0, min: 'strong' },
      'Version Control': { weight: 1.8, min: 'strong' },
      'Speed': { weight: 1.3, min: 'moderate' },
      'Cost Efficiency': { weight: 1.2, min: 'moderate' },
      'API Robustness': { weight: 1.5, min: 'strong' },
    },
    industry: 'Enterprise' 
  },
  'ensure-compliance': { 
    label: 'Ensure compliance', 
    categories: ['Guardrails'], 
    requirements: {
      'Accuracy': { weight: 2.8, min: 'strong' },
      'Enterprise Ready': { weight: 2.5, min: 'strong' },
      'Version Control': { weight: 1.8, min: 'strong' },
      'Integration Ease': { weight: 1.5, min: 'moderate' },
      'Customization': { weight: 1.3, min: 'moderate' },
      'Speed': { weight: 1.0, min: 'moderate' },
    },
    industry: 'Enterprise' 
  },
  'deploy-models': { 
    label: 'Deploy ML models', 
    categories: ['Deployment'], 
    requirements: {
      'Speed': { weight: 2.0, min: 'strong' },
      'API Robustness': { weight: 2.0, min: 'strong' },
      'Enterprise Ready': { weight: 1.8, min: 'strong' },
      'Cost Efficiency': { weight: 1.5, min: 'moderate' },
      'Integration Ease': { weight: 1.3, min: 'moderate' },
      'Batch Processing': { weight: 1.2, min: 'moderate' },
    },
    industry: 'Engineering' 
  },
  'fine-tune': { 
    label: 'Fine-tune models', 
    categories: ['Fine-tuning'], 
    requirements: {
      'Customization': { weight: 2.5, min: 'strong' },
      'Cost Efficiency': { weight: 1.8, min: 'moderate' },
      'Speed': { weight: 1.5, min: 'moderate' },
      'Open Source': { weight: 1.3, min: 'moderate' },
      'Version Control': { weight: 1.2, min: 'moderate' },
      'Integration Ease': { weight: 1.0, min: 'moderate' },
    },
    industry: 'Engineering' 
  },
  'process-documents': { 
    label: 'Process documents', 
    categories: ['Data Processing'], 
    requirements: {
      'Accuracy': { weight: 2.3, min: 'strong' },
      'Speed': { weight: 1.8, min: 'moderate' },
      'Batch Processing': { weight: 2.0, min: 'strong' },
      'Integration Ease': { weight: 1.5, min: 'moderate' },
      'Cost Efficiency': { weight: 1.2, min: 'moderate' },
      'Multimodal': { weight: 1.3, min: 'moderate' },
    },
    industry: 'Enterprise' 
  },
};

export const industryData = {
  'Marketing': { label: 'Marketing', tasks: ['write-content', 'generate-images', 'create-videos', 'generate-voice'], relevantCategories: ['Language Model', 'Image Generation', 'Audio/Voice', 'Video Generation'] },
  'Engineering': { label: 'Engineering', tasks: ['write-code', 'build-agent', 'build-rag', 'test-prompts', 'monitor-llm', 'deploy-models', 'fine-tune'], relevantCategories: ['Code Assistant', 'Language Model', 'Orchestration', 'Evaluation', 'Vector Database', 'Observability', 'Deployment', 'Fine-tuning', 'Agents'] },
  'Sales': { label: 'Sales', tasks: ['analyze-calls', 'research-leads', 'write-outreach'], relevantCategories: ['Language Model', 'Audio/Voice', 'Speech Recognition', 'Search'] },
  'Creative': { label: 'Creative', tasks: ['generate-images', 'create-videos', 'edit-photos', 'create-music', 'design-assets'], relevantCategories: ['Image Generation', 'Video Generation', 'Audio/Voice', 'Language Model'] },
  'Enterprise': { label: 'Enterprise', tasks: ['add-safety', 'manage-models', 'ensure-compliance', 'build-rag', 'process-documents'], relevantCategories: ['Language Model', 'Guardrails', 'Deployment', 'Vector Database', 'Observability', 'Data Processing'] },
};

export const categoryIcons = { 
  'Agents': '🤖',
  'Audio/Voice': '🎙️',
  'Code Assistant': '💻',
  'Data Processing': '📄',
  'Deployment': '🚀',
  'Embeddings': '🔢',
  'Evaluation': '📊',
  'Fine-tuning': '🎯',
  'Guardrails': '🛡️',
  'Image Generation': '🎨',
  'Language Model': '🧠', 
  'Observability': '👁️',
  'Orchestration': '🔗', 
  'Other': '📦',
  'Search': '🔍', 
  'Speech Recognition': '👂',
  'Vector Database': '📦',
  'Video Generation': '🎬',
};

// Level values for scoring - exceptional is rare and valuable
export const levelVal = { exceptional: 5, strong: 4, moderate: 3, limited: 2, weak: 1 };
export const levelMin = { exceptional: 5, strong: 4, moderate: 3, limited: 2, weak: 1 };

// ============================================
// TOOL DATA - 100 Tools from touchstone-AI-database
// ============================================

// Category colors for visualization
const categoryColors = {
  'Agents': '#8b5cf6', 'Audio/Voice': '#ec4899', 'Code Assistant': '#22c55e',
  'Data Processing': '#3b82f6', 'Deployment': '#f97316', 'Embeddings': '#06b6d4',
  'Evaluation': '#eab308', 'Fine-tuning': '#a855f7', 'Guardrails': '#ef4444',
  'Image Generation': '#f43f5e', 'Language Model': '#10b981', 'Observability': '#6366f1',
  'Orchestration': '#14b8a6', 'Other': '#71717a', 'Search': '#0ea5e9',
  'Speech Recognition': '#8b5cf6', 'Vector Database': '#64748b', 'Video Generation': '#d946ef',
};
const bg = (cat) => categoryColors[cat] || '#71717a';

// Logo URLs from touchstone-AI-database
const logos = {
  // Language Models
  'GPT-4': 'https://avatars.githubusercontent.com/u/14957082',
  'Claude': 'https://cdn.simpleicons.org/anthropic',
  'Gemini': 'https://cdn.simpleicons.org/google',
  'LLaMA': 'https://cdn.simpleicons.org/meta',
  'Mistral': 'https://avatars.githubusercontent.com/u/132372032',
  'Cohere': 'https://avatars.githubusercontent.com/u/54850923',
  'Command R+': 'https://avatars.githubusercontent.com/u/54850923',
  'Grok': 'https://cdn.simpleicons.org/x',
  'Inflection Pi': 'https://avatars.githubusercontent.com/u/125570875',
  'Reka': 'https://avatars.githubusercontent.com/u/147847949',
  
  // Image Generation
  'DALL-E': 'https://avatars.githubusercontent.com/u/14957082',
  'Stable Diffusion': 'https://avatars.githubusercontent.com/u/106524776',
  'Imagen': 'https://cdn.simpleicons.org/google',
  'Midjourney': 'https://cdn.simpleicons.org/midjourney',
  'Adobe Firefly': 'https://cdn.simpleicons.org/adobe',
  'Flux': 'https://avatars.githubusercontent.com/u/173aborat',
  'Ideogram': 'https://avatars.githubusercontent.com/u/135438671',
  'Leonardo.ai': 'https://avatars.githubusercontent.com/u/121283862',
  
  // Video Generation
  'Sora': 'https://avatars.githubusercontent.com/u/14957082',
  'Runway': 'https://cdn.simpleicons.org/runwayml',
  'Pika': 'https://avatars.githubusercontent.com/u/143577467',
  'Luma Dream Machine': 'https://avatars.githubusercontent.com/u/87743170',
  'Haiper': 'https://avatars.githubusercontent.com/u/158118053',
  'Kling': 'https://avatars.githubusercontent.com/u/75082151',
  
  // Audio/Voice
  'ElevenLabs': 'https://cdn.simpleicons.org/elevenlabs',
  'Whisper': 'https://avatars.githubusercontent.com/u/14957082',
  'Descript': 'https://avatars.githubusercontent.com/u/25212573',
  'Murf': 'https://avatars.githubusercontent.com/u/70820029',
  'PlayHT': 'https://avatars.githubusercontent.com/u/74789789',
  'Resemble.ai': 'https://avatars.githubusercontent.com/u/42566396',
  'AssemblyAI': 'https://cdn.simpleicons.org/assemblyai',
  'Deepgram': 'https://avatars.githubusercontent.com/u/26072506',
  
  // Code Assistant
  'GitHub Copilot': 'https://cdn.simpleicons.org/github',
  'Cursor': 'https://cdn.simpleicons.org/cursor',
  'Aider': 'https://avatars.githubusercontent.com/u/134048918',
  'Amazon CodeWhisperer': 'https://cdn.simpleicons.org/amazon',
  'Codeium': 'https://avatars.githubusercontent.com/u/116730386',
  'Cody': 'https://avatars.githubusercontent.com/u/16724977',
  'Continue': 'https://avatars.githubusercontent.com/u/133aborat',
  'Replit AI': 'https://cdn.simpleicons.org/replit',
  'Tabnine': 'https://avatars.githubusercontent.com/u/19544873',
  
  // Orchestration
  'LangChain': 'https://cdn.simpleicons.org/langchain',
  'LlamaIndex': 'https://avatars.githubusercontent.com/u/130722866',
  'Haystack': 'https://avatars.githubusercontent.com/u/51827949',
  'Semantic Kernel': 'https://avatars.githubusercontent.com/u/6154722',
  
  // Agents
  'AutoGen': 'https://avatars.githubusercontent.com/u/6154722',
  'CrewAI': 'https://avatars.githubusercontent.com/u/7918406',
  'LangGraph': 'https://cdn.simpleicons.org/langchain',
  'GPTs': 'https://avatars.githubusercontent.com/u/14957082',
  'n8n AI': 'https://cdn.simpleicons.org/n8n',
  'Make AI': 'https://cdn.simpleicons.org/integromat',
  'Zapier AI': 'https://cdn.simpleicons.org/zapier',
  
  // Vector Databases
  'Pinecone': 'https://avatars.githubusercontent.com/u/54333248',
  'Weaviate': 'https://avatars.githubusercontent.com/u/37794290',
  'Qdrant': 'https://avatars.githubusercontent.com/u/73504361',
  'Milvus': 'https://cdn.simpleicons.org/milvus',
  'Chroma': 'https://avatars.githubusercontent.com/u/117756271',
  'PGVector': 'https://cdn.simpleicons.org/postgresql',
  
  // Observability
  'Langfuse': 'https://avatars.githubusercontent.com/u/128686189',
  'LangSmith': 'https://cdn.simpleicons.org/langchain',
  'Weights & Biases': 'https://cdn.simpleicons.org/weightsandbiases',
  'Arize': 'https://avatars.githubusercontent.com/u/69502595',
  'Helicone': 'https://avatars.githubusercontent.com/u/126547688',
  
  // Evaluation
  'Promptfoo': 'https://avatars.githubusercontent.com/u/128686189',
  'Braintrust': 'https://avatars.githubusercontent.com/u/112635552',
  'Ragas': 'https://avatars.githubusercontent.com/u/132292774',
  'TruLens': 'https://avatars.githubusercontent.com/u/128300892',
  
  // Guardrails
  'Guardrails AI': 'https://avatars.githubusercontent.com/u/106301619',
  'NeMo Guardrails': 'https://cdn.simpleicons.org/nvidia',
  'LLM Guard': 'https://avatars.githubusercontent.com/u/138899583',
  'Rebuff': 'https://avatars.githubusercontent.com/u/129474782',
  
  // Deployment
  'Hugging Face': 'https://cdn.simpleicons.org/huggingface',
  'Replicate': 'https://cdn.simpleicons.org/replicate',

  'Modal': 'https://avatars.githubusercontent.com/u/89675610',
  'Anyscale': 'https://avatars.githubusercontent.com/u/40842654',
  'Baseten': 'https://avatars.githubusercontent.com/u/61949563',
  'Cerebras': 'https://avatars.githubusercontent.com/u/23408515',
  'Fireworks AI': 'https://avatars.githubusercontent.com/u/109911270',
  'Groq': 'https://avatars.githubusercontent.com/u/106956538',
  'SambaNova': 'https://avatars.githubusercontent.com/u/32020710',
  
  // Data Processing
  'LlamaParse': 'https://avatars.githubusercontent.com/u/130722866',
  'Docling': 'https://cdn.simpleicons.org/ibm',
  'Unstructured': 'https://avatars.githubusercontent.com/u/98758836',
  
  // Embeddings
  'OpenAI Embeddings': 'https://avatars.githubusercontent.com/u/14957082',
  'Cohere Embed': 'https://avatars.githubusercontent.com/u/54850923',
  'Jina Embeddings': 'https://avatars.githubusercontent.com/u/60539444',
  'Nomic Embed': 'https://avatars.githubusercontent.com/u/101579824',
  'Voyage AI': 'https://avatars.githubusercontent.com/u/140431645',
  
  // Fine-tuning

  'Unsloth': 'https://avatars.githubusercontent.com/u/150189782',
  'OpenPipe': 'https://avatars.githubusercontent.com/u/127973040',
  'Predibase': 'https://avatars.githubusercontent.com/u/69614629',
  
  // Search
  'Perplexity': 'https://cdn.simpleicons.org/perplexity',
  'Exa': 'https://avatars.githubusercontent.com/u/129157768',
  'Phind': 'https://avatars.githubusercontent.com/u/115093410',
  'Tavily': 'https://avatars.githubusercontent.com/u/128686789',
  'You.com': 'https://avatars.githubusercontent.com/u/71153398',
  
  // Other
  'ChatGPT Plugins': 'https://avatars.githubusercontent.com/u/14957082',
  'Claude Artifacts': 'https://cdn.simpleicons.org/anthropic',
};

export const getLogoUrl = (name) => logos[name] || null;

// ============================================
// TOOL DATA - Auto-generated from ui_export.json
// Globe and database always stay in sync
// ============================================
export const toolData = uiExport.tools.map(tool => ({
  id: tool.id,
  name: tool.name,
  category: tool.category || 'Other',
  provider: tool.provider || 'Unknown',
  role: tool.description || tool.tagline || '',
  description: tool.description || '',
  goodFitIf: tool.goodFitIf || '',
  notGoodFitIf: tool.notGoodFitIf || '',
  lastReviewed: tool.lastReviewed || '',
  verified: tool.logo?.verified ?? true,
  pricing: {
    model: tool.pricing?.model || 'unknown',
    free: tool.pricing?.free_tier ?? (tool.pricing?.model === 'free'),
  },
  bg: bg(tool.category || 'Other'),
}));

// Available software/tools that users might already have
export const availableSoftware = [
  // Communication
  { id: 'slack', name: 'Slack', category: 'Communication', icon: '💬' },
  { id: 'teams', name: 'Microsoft Teams', category: 'Communication', icon: '👥' },
  { id: 'discord', name: 'Discord', category: 'Communication', icon: '🎮' },
  { id: 'zoom', name: 'Zoom', category: 'Communication', icon: '📹' },

  // Productivity
  { id: 'notion', name: 'Notion', category: 'Productivity', icon: '📝' },
  { id: 'google-workspace', name: 'Google Workspace', category: 'Productivity', icon: '🔷' },
  { id: 'microsoft-365', name: 'Microsoft 365', category: 'Productivity', icon: '📊' },
  { id: 'airtable', name: 'Airtable', category: 'Productivity', icon: '📋' },
  { id: 'clickup', name: 'ClickUp', category: 'Productivity', icon: '✅' },

  // CRM & Sales
  { id: 'hubspot', name: 'HubSpot', category: 'CRM & Sales', icon: '🟠' },
  { id: 'salesforce', name: 'Salesforce', category: 'CRM & Sales', icon: '☁️' },
  { id: 'pipedrive', name: 'Pipedrive', category: 'CRM & Sales', icon: '📈' },

  // Automation
  { id: 'zapier', name: 'Zapier', category: 'Automation', icon: '⚡' },
  { id: 'make', name: 'Make', category: 'Automation', icon: '🔄' },
  { id: 'n8n', name: 'n8n', category: 'Automation', icon: '🔗' },

  // Design
  { id: 'figma', name: 'Figma', category: 'Design', icon: '🎨' },
  { id: 'adobe', name: 'Adobe Creative Cloud', category: 'Design', icon: '🅰️' },
  { id: 'canva', name: 'Canva', category: 'Design', icon: '🖼️' },

  // Development
  { id: 'github', name: 'GitHub', category: 'Development', icon: '🐙' },
  { id: 'vscode', name: 'VS Code', category: 'Development', icon: '💻' },
  { id: 'docker', name: 'Docker', category: 'Development', icon: '🐳' },
  { id: 'gitlab', name: 'GitLab', category: 'Development', icon: '🦊' },
  { id: 'cursor', name: 'Cursor', category: 'Development', icon: '⌨️' },

  // Project Management
  { id: 'jira', name: 'Jira', category: 'Project Management', icon: '🔵' },
  { id: 'asana', name: 'Asana', category: 'Project Management', icon: '🎯' },
  { id: 'linear', name: 'Linear', category: 'Project Management', icon: '📐' },
  { id: 'monday', name: 'Monday.com', category: 'Project Management', icon: '📅' },
  { id: 'trello', name: 'Trello', category: 'Project Management', icon: '📌' },

  // Cloud
  { id: 'aws', name: 'AWS', category: 'Cloud', icon: '☁️' },
  { id: 'gcp', name: 'Google Cloud', category: 'Cloud', icon: '🌩️' },
  { id: 'azure', name: 'Azure', category: 'Cloud', icon: '🔷' },
  { id: 'vercel', name: 'Vercel', category: 'Cloud', icon: '▲' },
  { id: 'netlify', name: 'Netlify', category: 'Cloud', icon: '🌐' },
  { id: 'supabase', name: 'Supabase', category: 'Cloud', icon: '⚡' },

  // Analytics & Observability
  { id: 'datadog', name: 'Datadog', category: 'Analytics', icon: '🐕' },
  { id: 'google-analytics', name: 'Google Analytics', category: 'Analytics', icon: '📊' },
  { id: 'mixpanel', name: 'Mixpanel', category: 'Analytics', icon: '📉' },
  { id: 'posthog', name: 'PostHog', category: 'Analytics', icon: '🦔' },
];

// User roles with their priorities
export const userRoles = [
  { 
    id: 'developer', 
    name: 'Developer', 
    icon: '👨‍💻',
    priorities: { 'Integration Ease': 1.5, 'API Robustness': 1.5, 'Open Source': 1.3, 'Speed': 1.2 },
    description: 'Focus on API quality, integration, and developer experience'
  },
  { 
    id: 'designer', 
    name: 'Designer', 
    icon: '🎨',
    priorities: { 'Output Quality': 1.6, 'Customization': 1.4, 'Multimodal': 1.3 },
    description: 'Focus on output quality and creative control'
  },
  { 
    id: 'marketer', 
    name: 'Marketer', 
    icon: '📣',
    priorities: { 'Output Quality': 1.4, 'Speed': 1.3, 'Cost Efficiency': 1.3, 'Batch Processing': 1.2 },
    description: 'Focus on quality content at scale'
  },
  { 
    id: 'data-scientist', 
    name: 'Data Scientist', 
    icon: '📊',
    priorities: { 'Accuracy': 1.6, 'Customization': 1.4, 'Open Source': 1.3, 'Version Control': 1.2 },
    description: 'Focus on accuracy and reproducibility'
  },
  { 
    id: 'product-manager', 
    name: 'Product Manager', 
    icon: '📋',
    priorities: { 'Integration Ease': 1.4, 'Enterprise Ready': 1.3, 'Cost Efficiency': 1.2 },
    description: 'Focus on team adoption and ROI'
  },
  { 
    id: 'startup-founder', 
    name: 'Startup Founder', 
    icon: '🚀',
    priorities: { 'Cost Efficiency': 1.5, 'Speed': 1.4, 'Integration Ease': 1.3 },
    description: 'Focus on speed and cost-effectiveness'
  },
  { 
    id: 'enterprise-architect', 
    name: 'Enterprise Architect', 
    icon: '🏢',
    priorities: { 'Enterprise Ready': 1.6, 'API Robustness': 1.4, 'Version Control': 1.3 },
    description: 'Focus on security, compliance, and scalability'
  },
  { 
    id: 'researcher', 
    name: 'Researcher', 
    icon: '🔬',
    priorities: { 'Accuracy': 1.5, 'Output Quality': 1.4, 'Open Source': 1.3, 'Context Window': 1.2 },
    description: 'Focus on accuracy and depth of analysis'
  },
];

// Budget tiers
export const budgetTiers = [
  { id: 'free', name: 'Free Only', description: 'Only free tools', maxMonthly: 0 },
  { id: 'low', name: 'Budget', description: 'Under $50/mo', maxMonthly: 50 },
  { id: 'medium', name: 'Standard', description: 'Under $200/mo', maxMonthly: 200 },
  { id: 'enterprise', name: 'Enterprise', description: 'Any budget', maxMonthly: Infinity },
  { id: 'any', name: 'Show All', description: 'No budget filter', maxMonthly: null },
];
